import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import {
    Drawer,
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormControlLabel,
    Checkbox,
    FormHelperText,
    Divider,
    InputAdornment,
} from '@mui/material';
import { SCUIButton } from '../../../atomic/atoms';
import {
    Edit as EditIcon,
    Add as AddIcon,
    Psychology as PsychologyIcon,
    Code as CodeIcon,
    Category as CategoryIcon,
    SmartToy as SmartToyIcon,
    TextFields as TextFieldsIcon,
    ToggleOn as ToggleOnIcon,
} from '@mui/icons-material';
import {
    AIPromptCategory,
    AIModelType,
    AIPromptCategoryOptions,
    AIModelTypeOptions,
} from '../../../types/enums/ai-prompt-enums';
import { AIPromptResponse } from '../../../services/query/aiPrompt/AIPromptQuery.services';
import { toCamelCaseCode } from '../../../utils/stringHelpers';

interface AIPromptFormDrawerProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: AIPromptFormData) => void;
    editData?: AIPromptResponse | null;
    loading?: boolean;
}

export interface AIPromptFormData {
    name: string;
    code: string;
    category: AIPromptCategory;
    modelType: AIModelType;
    promptText: string;
    isSystemPrompt: boolean;
    isActive: boolean;
}

const AIPromptFormDrawer: React.FC<AIPromptFormDrawerProps> = ({
    open,
    onClose,
    onSubmit,
    editData,
    loading = false,
}) => {
    const { t } = useTranslation();

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm<AIPromptFormData>({
        defaultValues: {
            name: '',
            code: '',
            category: AIPromptCategory.ImageGeneration,
            modelType: AIModelType.EachLabs,
            promptText: '',
            isSystemPrompt: false,
            isActive: true,
        },
    });

    // Name değiştiğinde code'u otomatik oluştur
    const nameValue = watch('name');
    useEffect(() => {
        if (!editData && nameValue) {
            const generatedCode = toCamelCaseCode(nameValue);
            setValue('code', generatedCode);
        }
    }, [nameValue, editData, setValue]);

    useEffect(() => {
        if (editData) {
            reset({
                name: editData.name,
                code: editData.code || '',
                category: editData.category as AIPromptCategory,
                modelType: editData.modelType as AIModelType,
                promptText: editData.promptText,
                isSystemPrompt: editData.isSystemPrompt,
                isActive: editData.isActive,
            });
        } else {
            reset({
                name: '',
                code: '',
                category: AIPromptCategory.ImageGeneration,
                modelType: AIModelType.EachLabs,
                promptText: '',
                isSystemPrompt: false,
                isActive: true,
            });
        }
    }, [editData, reset, open]);

    const handleFormSubmit = (data: AIPromptFormData) => {
        onSubmit(data);
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: { width: { xs: '100%', sm: 600 } },
                },
            }}
        >
            <Box sx={{ p: 3 }}>
                {/* Header with Icon */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 2,
                    }}
                >
                    {editData ? (
                        <EditIcon color="primary" sx={{ fontSize: 32 }} />
                    ) : (
                        <AddIcon color="primary" sx={{ fontSize: 32 }} />
                    )}
                    <Typography variant="h5">{editData ? t('aiPrompt.edit') : t('aiPrompt.addNew')}</Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Prompt Name */}
                        <Controller
                            name="name"
                            control={control}
                            rules={{
                                required: t('aiPrompt.validation.nameRequired'),
                                maxLength: {
                                    value: 200,
                                    message: t('aiPrompt.validation.nameMaxLength'),
                                },
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t('aiPrompt.name')}
                                    fullWidth
                                    required
                                    error={!!errors.name}
                                    helperText={errors.name?.message}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <TextFieldsIcon color="action" />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            )}
                        />

                        {/* Prompt Code */}
                        <Controller
                            name="code"
                            control={control}
                            rules={{
                                required: t('aiPrompt.validation.codeRequired'),
                                maxLength: {
                                    value: 100,
                                    message: t('aiPrompt.validation.codeMaxLength'),
                                },
                                pattern: {
                                    value: /^[a-z][a-zA-Z0-9]*$/,
                                    message: t('aiPrompt.validation.codePattern'),
                                },
                            }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t('aiPrompt.code')}
                                    fullWidth
                                    required
                                    error={!!errors.code}
                                    helperText={errors.code?.message || t('aiPrompt.codeHelper')}
                                    disabled={!!editData}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <CodeIcon color="action" />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            )}
                        />

                        {/* Category */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <CategoryIcon fontSize="small" color="action" />
                                <Typography variant="caption" color="text.secondary">
                                    {t('aiPrompt.category')}
                                </Typography>
                            </Box>
                            <Controller
                                name="category"
                                control={control}
                                rules={{ required: t('aiPrompt.validation.categoryRequired') }}
                                render={({ field }) => (
                                    <FormControl fullWidth required error={!!errors.category}>
                                        <InputLabel>{t('aiPrompt.category')}</InputLabel>
                                        <Select {...field} label={t('aiPrompt.category')}>
                                            {AIPromptCategoryOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {t(option.label)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.category && <FormHelperText>{errors.category.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Box>

                        {/* Model Type */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <SmartToyIcon fontSize="small" color="action" />
                                <Typography variant="caption" color="text.secondary">
                                    {t('aiPrompt.modelType')}
                                </Typography>
                            </Box>
                            <Controller
                                name="modelType"
                                control={control}
                                rules={{ required: t('aiPrompt.validation.modelTypeRequired') }}
                                render={({ field }) => (
                                    <FormControl fullWidth required error={!!errors.modelType}>
                                        <InputLabel>{t('aiPrompt.modelType')}</InputLabel>
                                        <Select {...field} label={t('aiPrompt.modelType')}>
                                            {AIModelTypeOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {t(option.label)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.modelType && (
                                            <FormHelperText>{errors.modelType.message}</FormHelperText>
                                        )}
                                    </FormControl>
                                )}
                            />
                        </Box>

                        {/* Prompt Text */}
                        <Controller
                            name="promptText"
                            control={control}
                            rules={{ required: t('aiPrompt.validation.promptTextRequired') }}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t('aiPrompt.promptText')}
                                    fullWidth
                                    required
                                    multiline
                                    rows={6}
                                    error={!!errors.promptText}
                                    helperText={errors.promptText?.message}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment
                                                    position="start"
                                                    sx={{ alignSelf: 'flex-start', mt: 2 }}
                                                >
                                                    <PsychologyIcon color="action" />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            )}
                        />

                        {/* Settings Section */}
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <ToggleOnIcon color="action" />
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t('general.settings')}
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {/* Is System Prompt */}
                            <Controller
                                name="isSystemPrompt"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        }
                                        label={t('aiPrompt.isSystemPrompt')}
                                    />
                                )}
                            />
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                            <SCUIButton
                                text={t('general.close')}
                                onClick={onClose}
                                buttonType="button"
                                type="primary"
                                sizeType="fixed"
                                size="l"
                                disabled={loading}
                            />
                            <SCUIButton
                                text={t('general.save')}
                                buttonType="submit"
                                type="primary"
                                sizeType="fixed"
                                size="l"
                                disabled={loading}
                            />
                        </Box>
                    </Box>
                </form>
            </Box>
        </Drawer>
    );
};

export default AIPromptFormDrawer;
